import type { PageSection, ReportType } from '../../models/end-client-reporting-package';
import { exampleExplainer, exampleLadder } from './report-blocks/block-examples';
import type { AnswerOption, RespondentRecord, SurveyLayout } from './report-aggregation';

/**
 * A small invented study the module guide draws its examples from: a coffee-shop brand tracker,
 * built so every module has a question shaped for it -- a funnel that narrows, a battery on one
 * scale, a ranking grid, two measures of the same items, brands against attributes.
 *
 * Generated rather than checked in, from a fixed seed, so the examples are the same on every
 * screen and every load, and stay small: six hundred respondents built in a few milliseconds when
 * the guide first opens, and never before.
 *
 * Nothing here is real data, and nothing outside the guide reads it.
 */

export const SAMPLE_BRAND = 'Brew & Co.';
const BRANDS = [SAMPLE_BRAND, 'Bean There', 'Daily Grind', 'Roastery', 'Kettle House'];
const ATTRIBUTES = ['Taste', 'Price', 'Speed of service', 'Ethical sourcing', 'Atmosphere', 'Rewards'];
const SAMPLE_SIZE = 600;

/** mulberry32: a few lines, fast, and the same sequence for the same seed everywhere. */
function seeded(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function options(labels: string[], codes?: number[]): AnswerOption[] {
  return labels.map((label, i) => ({ value: codes?.[i] ?? i + 1, label }));
}

/** A code picked by weight, the weights in the options' own order. */
function pick(random: () => number, weights: number[]): number {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let roll = random() * total;
  for (let i = 0; i < weights.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return i;
  }
  return weights.length - 1;
}

const AGREE = ['Strongly agree', 'Agree', 'Neither', 'Disagree', 'Strongly disagree'];
const STATEMENTS = [
  'It is worth paying more for better coffee',
  'I choose a coffee shop by where I happen to be',
  'Sustainability matters when I buy coffee',
  'Loyalty apps make me visit more often',
  'Independent shops have better coffee than chains',
];
const TRADE_OFFS: [string, string][] = [
  ['Speed', 'Quality'],
  ['Price', 'Experience'],
  ['Familiar favourites', 'New flavours'],
  ['Takeaway', 'Stay in'],
];
const LADDER_RUNGS = exampleLadder().rungNames ?? [];
const WORDS = [
  'smooth', 'friendly', 'quick', 'pricey', 'cosy', 'strong', 'reliable', 'busy', 'tasty',
  'consistent', 'welcoming', 'bitter', 'fresh', 'modern', 'convenient',
];

function buildLayout(): SurveyLayout {
  const layout: SurveyLayout = {
    gender: { kind: 'coded', label: 'Gender', column: 'gender', options: options(['Male', 'Female']) },
    age: { kind: 'coded', label: 'Age', column: 'age', options: options(['18-34', '35-54', '55+']) },
    region: { kind: 'coded', label: 'Region', column: 'region', options: options(['North', 'South', 'East', 'West']) },
    habit: {
      kind: 'coded', label: 'How often do you buy coffee out?', column: 'habit',
      options: options(['Daily', 'Weekly', 'Occasionally']),
    },
    satisfaction: {
      kind: 'coded', label: `How satisfied are you with ${SAMPLE_BRAND}?`, column: 'satisfaction',
      options: options(['Very satisfied', 'Fairly satisfied', 'Neither', 'Fairly dissatisfied', 'Very dissatisfied', 'Don\'t know'], [1, 2, 3, 4, 5, 9]),
    },
    recommend: {
      kind: 'coded', label: `How likely are you to recommend ${SAMPLE_BRAND}?`, column: 'recommend',
      options: Array.from({ length: 11 }, (_, i) => ({
        value: i,
        label: i === 0 ? '0 - Not at all likely' : i === 10 ? '10 - Extremely likely' : String(i),
      })),
    },
    preferred: {
      kind: 'coded', label: 'Which coffee shop do you use most often?', column: 'preferred', options: options(BRANDS),
    },
    reasons: {
      kind: 'multi', label: 'Why do you choose the coffee shop you use most?',
      columns: ['Taste', 'Price', 'Convenience', 'Ethical sourcing', 'Atmosphere', 'Loyalty rewards']
        .map((label, i) => ({ column: `reasons_${i + 1}`, label })),
    },
    aware: { kind: 'coded', label: `Aware of ${SAMPLE_BRAND}`, column: 'aware', options: options(['Yes', 'No']) },
    considered: { kind: 'coded', label: `Would consider ${SAMPLE_BRAND}`, column: 'considered', options: options(['Yes', 'No']) },
    tried: { kind: 'coded', label: `Have tried ${SAMPLE_BRAND}`, column: 'tried', options: options(['Yes', 'No']) },
    regular: { kind: 'coded', label: `Buy ${SAMPLE_BRAND} regularly`, column: 'regular', options: options(['Yes', 'No']) },
    importance: {
      kind: 'numeric', label: 'How important is each to you? (0-10)',
      columns: ATTRIBUTES.map((label, i) => ({ column: `importance_${i + 1}`, label })),
    },
    performance: {
      kind: 'numeric', label: `How well does ${SAMPLE_BRAND} do on each? (0-10)`,
      columns: ATTRIBUTES.map((label, i) => ({ column: `performance_${i + 1}`, label })),
    },
    word: { kind: 'text', label: `What one word describes ${SAMPLE_BRAND}?`, column: 'word' },
  };

  STATEMENTS.forEach((label, i) => {
    layout[`agree_${i + 1}`] = {
      kind: 'coded', label, column: `agree_${i + 1}`, options: options(AGREE), group: 'How much do you agree?',
    };
  });
  TRADE_OFFS.forEach(([left, right], i) => {
    layout[`trade_${i + 1}`] = {
      kind: 'coded', label: `${left} or ${right.toLowerCase()}?`, column: `trade_${i + 1}`,
      options: options([`Prefer ${left.toLowerCase()}`, 'Somewhere in between', `Prefer ${right.toLowerCase()}`]),
    };
  });
  ATTRIBUTES.forEach((label, i) => {
    layout[`rank_${i + 1}`] = {
      kind: 'coded', label, column: `rank_${i + 1}`,
      options: options(ATTRIBUTES.map((_, r) => `Ranked ${r + 1}`)), group: 'Rank what matters most',
    };
    layout[`own_${i + 1}`] = {
      kind: 'multi', label, group: 'Which shops do you associate with each?',
      columns: BRANDS.map((brand, b) => ({ column: `own_${i + 1}_${b + 1}`, label: brand })),
    };
  });
  BRANDS.forEach((brand, i) => {
    layout[`ladder_${i + 1}`] = {
      kind: 'coded', label: brand, column: `ladder_${i + 1}`, options: options(LADDER_RUNGS),
      group: 'Your relationship with each shop',
    };
  });
  return layout;
}

function buildRecords(): RespondentRecord[] {
  const random = seeded(20261024);
  const records: RespondentRecord[] = [];

  for (let n = 0; n < SAMPLE_SIZE; n++) {
    const record: RespondentRecord = {};
    const young = random() < 0.36;
    const age = young ? 0 : pick(random, [0, 5, 4]);
    const habit = pick(random, young ? [5, 4, 2] : [3, 4, 4]);
    // How much this respondent likes the brand: drives satisfaction, recommendation and the ladder,
    // so the modules that read those agree with each other the way a real study's would.
    const warmth = Math.min(1, Math.max(0, 0.55 + (habit === 0 ? 0.18 : habit === 2 ? -0.15 : 0)
      + (young ? 0.08 : -0.04) + (random() - 0.5) * 0.6));

    record['gender'] = String(1 + pick(random, [48, 52]));
    record['age'] = String(age + 1);
    record['region'] = String(1 + pick(random, [3, 2, 3, 2]));
    record['habit'] = String(habit + 1);
    record['qtime'] = String(Math.round(420 + random() * 480));

    const satisfaction = random() < 0.04 ? 9 : 1 + Math.min(4, Math.floor((1 - warmth) * 5 + (random() - 0.5) * 1.4));
    record['satisfaction'] = String(Math.max(1, satisfaction));
    record['recommend'] = String(Math.max(0, Math.min(10, Math.round(warmth * 10 + (random() - 0.5) * 3))));
    record['preferred'] = String(1 + pick(random, [warmth * 6 + 1, 2.5, 2, 1.5, 1]));

    ['reasons_1', 'reasons_2', 'reasons_3', 'reasons_4', 'reasons_5', 'reasons_6'].forEach((column, i) => {
      const chance = [0.72, 0.48, 0.58, young ? 0.38 : 0.22, 0.3, habit === 0 ? 0.42 : 0.2][i];
      record[column] = random() < chance ? column : '0';
    });

    // A funnel that genuinely narrows: each stage is only asked of those who reached the last.
    const stages = ['aware', 'considered', 'tried', 'regular'];
    const keep = [0.86, 0.62 + warmth * 0.2, 0.55 + warmth * 0.2, 0.35 + warmth * 0.3];
    let reached = true;
    stages.forEach((column, i) => {
      if (!reached) return;
      reached = random() < keep[i];
      record[column] = reached ? '1' : '2';
    });

    STATEMENTS.forEach((_, i) => {
      const lean = [0.62, 0.45, young ? 0.66 : 0.48, habit === 0 ? 0.7 : 0.42, 0.55][i];
      const agree = random() < lean;
      record[`agree_${i + 1}`] = String(agree ? 1 + pick(random, [3, 5]) : 3 + pick(random, [3, 4, 2]));
    });
    TRADE_OFFS.forEach((_, i) => {
      const left = [0.38, 0.55, 0.6, 0.66][i];
      record[`trade_${i + 1}`] = String(random() < 0.16 ? 2 : random() < left ? 1 : 3);
    });

    // A full ranking: the attributes ordered by a noisy score, so taste usually wins and rewards
    // rarely does, without any two respondents being alike.
    const appeal = [1.6, 1.25, 1, young ? 1.1 : 0.7, 0.8, habit === 0 ? 0.95 : 0.5];
    ATTRIBUTES
      .map((_, i) => ({ i, score: appeal[i] + random() * 1.3 }))
      .sort((a, b) => b.score - a.score)
      .forEach((entry, rank) => { record[`rank_${entry.i + 1}`] = String(rank + 1); });

    const strengths = [
      [0.62, 0.3, 0.45, 0.5, 0.25], [0.22, 0.48, 0.55, 0.2, 0.4], [0.35, 0.52, 0.42, 0.18, 0.3],
      [0.4, 0.2, 0.15, 0.58, 0.22], [0.55, 0.42, 0.18, 0.6, 0.35], [0.5, 0.25, 0.3, 0.15, 0.2],
    ];
    ATTRIBUTES.forEach((_, i) => BRANDS.forEach((__, b) => {
      record[`own_${i + 1}_${b + 1}`] = random() < strengths[i][b] ? `own_${i + 1}_${b + 1}` : '0';
    }));

    const importance = [8.6, 7.4, 6.8, young ? 6.9 : 5.6, 5.9, 5.1];
    const performance = [8.1, 5.4, 6.1, 7.2, 7.8, 6.6];
    ATTRIBUTES.forEach((_, i) => {
      record[`importance_${i + 1}`] = String(Math.round(Math.min(10, Math.max(0, importance[i] + (random() - 0.5) * 3))));
      record[`performance_${i + 1}`] = String(Math.round(Math.min(10, Math.max(0, performance[i] + warmth - 0.55 + (random() - 0.5) * 3))));
    });

    // The relationship ladder: further up it the warmer the respondent, with the brand itself
    // strongest and the smaller shops mostly unknown.
    // The last two rungs are the equity deficit -- unaware, then detractors -- and sit outside the
    // climb, so they are drawn separately rather than off the end of it.
    const unaware = LADDER_RUNGS.length - 2;
    BRANDS.forEach((_, b) => {
      const pull = [warmth, 0.5, 0.42, 0.3, 0.22][b];
      const climb = Math.min(unaware - 1, Math.max(0, Math.floor((1 - pull) * 6 + (random() - 0.5) * 2.5)));
      const rung = random() < 0.08 + (1 - pull) * 0.35
        ? (random() < 0.15 ? unaware + 1 : unaware)
        : climb;
      record[`ladder_${b + 1}`] = String(rung + 1);
    });

    if (random() < 0.7) {
      const warm = WORDS.slice(0, 11);
      const pool = warmth > 0.5 ? warm : WORDS;
      record['word'] = pool[Math.floor(random() * pool.length)];
    }

    records.push(record);
  }
  return records;
}

export interface GuideSample {
  layout: SurveyLayout;
  records: RespondentRecord[];
  /** Every question, under its own name: the guide's examples need no renaming. */
  variableMap: Record<string, string>;
}

let cached: GuideSample | null = null;

/** Built once, on first use, and shared by every example after. */
export function guideSample(): GuideSample {
  if (!cached) {
    const layout = buildLayout();
    cached = {
      layout,
      records: buildRecords(),
      variableMap: Object.fromEntries(Object.keys(layout).map(key => [key, key])),
    };
  }
  return cached;
}

const keys = (prefix: string, count: number) => Array.from({ length: count }, (_, i) => `${prefix}_${i + 1}`);

/**
 * The block each module's example is drawn from, configured the way a researcher would use it on
 * this study. Exhaustive, so a new module cannot ship without showing what it is for.
 */
const EXAMPLE_DATA: { [T in ReportType]: { header: string; data: Extract<PageSection, { reportType: T }>['reportExtraData'] } } = {
  'study-overview': {
    header: 'About this study',
    data: {
      market: 'United Kingdom',
      incidenceRate: '64%',
      notesHeading: 'Method',
      notes: 'Online survey of adults who buy coffee out at least occasionally.\n- Fieldwork: September 2026\n- Quotas on age, gender and region',
    },
  },
  'headline-stats': {
    header: 'At a glance',
    data: {
      boxes: [
        { value: '86%', label: 'aware of Brew & Co.' },
        { value: '+24', label: 'net promoter score' },
        { value: '1 in 3', label: 'buy it regularly' },
        { value: 'Taste', label: 'the top reason to choose' },
      ],
      takeaways: [
        'Awareness is near-universal; conversion from trial to habit is the gap.',
        'Younger buyers lead on sustainability -- and on loyalty-app use.',
      ],
    },
  },
  'text': {
    header: 'About this section',
    data: {
      html: '<p>This section looks at <strong>how often people buy coffee out</strong>, and where.</p>'
        + '<ul><li>Frequency is asked of everyone.</li><li>Venue is asked of anyone who buys at least monthly.</li></ul>',
    },
  },
  // No picture in a guide sample: an upload belongs to one report.
  'image': {
    header: 'The new cup design',
    data: { altText: 'The redesigned takeaway cup' },
  },
  'action-list': {
    header: 'What to do next',
    data: {
      actions: [
        { text: 'Convert triallists with a first-month rewards offer', priority: 'high' },
        { text: 'Lead the 18-34 creative with ethical sourcing', priority: 'high' },
        { text: 'Close the price-perception gap with Daily Grind', priority: 'medium' },
        { text: 'Test seating upgrades in two Northern stores', priority: 'low' },
      ],
    },
  },
  'kpi-score': {
    header: 'Net Promoter Score',
    data: {
      variableKey: 'recommend',
      label: 'Net Promoter Score',
      topBand: 'last',
      bands: [{ label: 'Promoters', options: 2 }, { label: 'Passives', options: 2 }, { label: 'Detractors', options: 7 }],
      metric: 'net',
      netLabel: 'Promoters',
    },
  },
  'segment-tiles': {
    header: 'Satisfied, by region',
    data: { variableKey: 'satisfaction', groupBy: 'region', countedAnswers: ['Very satisfied', 'Fairly satisfied'] },
  },
  'bar-chart': {
    header: 'Why people choose their coffee shop',
    data: { variableKeys: ['reasons'], sortDescending: true },
  },
  'pie-chart': { header: 'Coffee shop used most often', data: { variableKey: 'preferred' } },
  'donut-chart': { header: 'How often people buy coffee out', data: { variableKey: 'habit' } },
  'stacked-scale': {
    header: 'Attitudes to coffee',
    data: {
      variableKeys: keys('agree', 5),
      bands: [{ label: 'Agree', options: 2 }, { label: 'Neither', options: 1 }, { label: 'Disagree', options: 2 }],
      topBand: 'first',
      showNet: true,
      netLabel: 'Agree',
    },
  },
  'filter-comparison': { header: 'Daily buyers against everyone', data: { variableKey: 'reasons' } },
  'subgroup-panels': {
    header: 'Satisfaction by age',
    data: { variableKey: 'satisfaction', groupBy: 'age' },
  },
  'crosstab': {
    header: 'Satisfaction by age and habit',
    data: {
      variableKey: 'satisfaction',
      groupBy: 'age',
      subgroups: [
        { variableKey: 'age', answer: '18-34' }, { variableKey: 'age', answer: '35-54' }, { variableKey: 'age', answer: '55+' },
        { variableKey: 'habit', answer: 'Daily' }, { variableKey: 'habit', answer: 'Weekly' }, { variableKey: 'habit', answer: 'Occasionally' },
      ],
      sigTest: 'columns',
      nets: [{ label: 'Satisfied', answers: ['Very satisfied', 'Fairly satisfied'] }],
      showMean: true,
      meanExcludes: ['Don\'t know'],
    },
  },
  'diverging-bars': { header: 'What people trade off', data: { variableKeys: keys('trade', 4) } },
  'tiered-ranking': {
    header: 'What matters most',
    data: { variableKeys: keys('rank', 6), topRanks: 3, tierCount: 2, tierLabels: ['Must-haves', 'Differentiators'] },
  },
  'funnel': {
    header: `The ${SAMPLE_BRAND} funnel`,
    data: { variableKeys: ['aware', 'considered', 'tried', 'regular'], countedAnswers: ['Yes'] },
  },
  'ownership-matrix': {
    header: 'Who owns what',
    data: { variableKeys: keys('own', 6), priorityKeys: keys('rank', 6), topRanks: 3 },
  },
  'heatmap': { header: 'Where people looked on the menu board', data: { variableKey: '' } },
  'stage-matrix': {
    header: 'Brand relationships',
    data: { ...exampleLadder(), variableKeys: keys('ladder', 5) },
  },
  'stage-explainer': { header: 'Reading the ladder', data: exampleExplainer() },
  'scatter-plot': {
    header: 'Importance against performance',
    data: {
      variableKeys: ['importance', 'performance'],
      xLabel: 'Importance to buyers',
      yLabel: `${SAMPLE_BRAND} performance`,
      quadrantLabel: 'Strengths to protect',
    },
  },
  'open-end-explorer': { header: `One word for ${SAMPLE_BRAND}`, data: { variableKey: 'word', pageSize: 8 } },
  'word-cloud': { header: `One word for ${SAMPLE_BRAND}`, data: { variableKey: 'word', minCount: 1 } },
};

/** The example block for one module, fresh each time. */
export function exampleBlock(reportType: ReportType): PageSection {
  const example = EXAMPLE_DATA[reportType];
  return {
    id: `guide-${reportType}`,
    header: example.header,
    segmentLocation: 'full',
    reportType,
    reportExtraData: JSON.parse(JSON.stringify(example.data)),
  } as PageSection;
}

/** Modules that cannot be drawn from invented data -- the heatmap needs the survey's own image. */
export function hasLiveExample(reportType: ReportType): boolean {
  return reportType !== 'heatmap';
}

/** The respondents a filter-comparison example compares with everyone: the daily buyers. */
export function sampleFilter(): { key: string; value: string } {
  return { key: 'habit', value: '1' };
}
